import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaFilm, FaHeart } from 'react-icons/fa';
import axios from 'axios';
import './PublicUserProfile.css';
import AppDownloadBanner from './AppDownloadBanner';

function CompatInvite() {
  const { inviterId } = useParams();
  const [inviterData, setInviterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInviterProfile = async () => {
      try {
        setLoading(true);
        // Appel API public : seules les infos publiques (pseudo/avatar) sont nécessaires ici
        const response = await axios.get(`/api/get-profile?user_id=${inviterId}`, {
          withCredentials: false
        });
        setInviterData(response.data);
      } catch (err) {
        console.error('Error fetching inviter profile:', err);
        setError('Invitation introuvable');
      } finally {
        setLoading(false);
      }
    };

    if (inviterId) {
      fetchInviterProfile();
    }
  }, [inviterId]);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = () => /Android/.test(navigator.userAgent);
  const isDesktop = () => !isIOS() && !isAndroid();

  const renderAppButton = () => {
    if (isIOS() || isAndroid()) {
      return (
        <a href={`cinemapp://compat-invite/${inviterId}`} className="prominent-app-button">
          🎬 Voir ma compatibilité
        </a>
      );
    } else if (isDesktop()) {
      return (
        <button
          className="prominent-app-button desktop-download"
          onClick={() => {
            alert('📱 Téléchargez CinéList sur votre mobile pour voir votre compatibilité !\n\n🍎 iOS: App Store\n🤖 Android: Play Store');
          }}
        >
          📲 Télécharger CinéList
        </button>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <h2>Chargement de l'invitation...</h2>
        </div>
      </div>
    );
  }

  if (error || !inviterData) {
    return (
      <div className="profile-page">
        <AppDownloadBanner inviterId={inviterId} />
        <div className="error-container">
          <h2>Invitation non disponible</h2>
          <p>{error || "Cette invitation n'est plus valide."}</p>
          <div className="download-suggestion">
            <h3>Découvrez CinéList</h3>
            <p>Téléchargez l'app pour noter des films et comparer vos goûts avec vos amis !</p>
          </div>
        </div>
      </div>
    );
  }

  const inviterName = inviterData.pseudo || 'Un ami';

  return (
    <div className="profile-page">
      <Helmet>
        <meta
          name="apple-itunes-app"
          content={`app-id=6740208004, app-argument=https://films.ahkapp.fr/compat-invite/${inviterId}`}
        />
        <meta property="og:title" content={`${inviterName} veut connaître ta compatibilité ciné`} />
        <meta
          property="og:description"
          content={`${inviterName} te défie de comparer vos goûts de films et séries sur CinéList !`}
        />
        <meta
          property="og:image"
          content={inviterData.avatar_url || 'https://films.ahkapp.fr/logo512.png'}
        />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="400" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:url" content={`https://films.ahkapp.fr/compat-invite/${inviterId}`} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <AppDownloadBanner inviterId={inviterId} />

      <div className="public-profile-header">
        <div className="profile-avatar-section">
          <img
            src={inviterData.avatar_url || '/logo.png'}
            alt={inviterName}
            className="profile-avatar"
          />
          <h2>{inviterName} te défie !</h2>
        </div>

        <div className="app-button-section">
          {renderAppButton()}
        </div>
      </div>

      <div className="app-promotion-section">
        <div className="app-promo-card">
          <h3>🎬 Compare tes goûts ciné avec {inviterName}</h3>
          <div className="promo-features">
            <div className="promo-feature">
              <FaHeart /> Score de compatibilité
            </div>
            <div className="promo-feature">
              <FaStar /> Vos accords et désaccords
            </div>
            <div className="promo-feature">
              <FaFilm /> Des recommandations en commun
            </div>
          </div>
          <p>
            Installe l'app CinéList, note quelques films, et découvre instantanément
            à quel point tes goûts ressemblent à ceux de {inviterName} !
          </p>
        </div>
      </div>

      <div className="limited-access-notice">
        <h4>Comment ça marche</h4>
        <p>
          Télécharge l'app, crée ton compte et note au moins 5 films ou séries.
          Ta comparaison avec {inviterName} se débloquera automatiquement.
        </p>
      </div>
    </div>
  );
}

export default CompatInvite;
